import React from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Switch, FormControlLabel,
    Stack, Divider, Slider, Tooltip
} from '@mui/material';
import {
    Speed as TargetIcon,
    Settings as PolicyIcon,
    CloudQueue as WeatherIcon,
    VerifiedUser as ProtectIcon
} from '@mui/icons-material';

interface NationalParameterEditorProps {
    buildTarget: number;
    onTargetChange: (value: number) => void;
    policyToggles: Record<string, boolean>;
    onToggleChange: (key: string, value: boolean) => void;
}

const NationalParameterEditor: React.FC<NationalParameterEditorProps> = ({
    buildTarget,
    onTargetChange,
    policyToggles,
    onToggleChange
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                border: '1px dashed',
                borderColor: 'primary.light',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    opacity: 0.05,
                    transform: 'rotate(-15deg)'
                }}
            >
                <TargetIcon sx={{ fontSize: 120, color: 'primary.main' }} />
            </Box>

            <Grid container spacing={4}>
                {/* Build Target Section */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TargetIcon color="primary" fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                National Build Target
                            </Typography>
                        </Box>

                        <Box sx={{ px: 1, pt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">Production Volume</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    {buildTarget.toLocaleString()} Units
                                </Typography>
                            </Box>
                            <Slider
                                value={buildTarget}
                                onChange={(_, v) => onTargetChange(v as number)}
                                min={0}
                                max={150000}
                                step={100}
                                valueLabelDisplay="auto"
                                sx={{
                                    '& .MuiSlider-thumb': {
                                        width: 16, height: 16,
                                        '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)' },
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    variant="outlined"
                                    label="Custom Target"
                                    value={buildTarget}
                                    onChange={(e) => onTargetChange(parseInt(e.target.value) || 0)}
                                    sx={{ bgcolor: 'white' }}
                                    inputProps={{ style: { fontSize: '13px', fontWeight: 600 } }}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Grid>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 0.5 }} />

                {/* Policy Toggles Section */}
                <Grid item xs={12} md={5.5}>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PolicyIcon color="primary" fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Strategic Policy Toggles
                            </Typography>
                        </Box>

                        <Stack spacing={1}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    bgcolor: policyToggles.weather_priority ? 'rgba(76, 175, 80, 0.05)' : 'white',
                                    borderColor: policyToggles.weather_priority ? 'success.light' : 'divider',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            color="success"
                                            checked={!!policyToggles.weather_priority}
                                            onChange={(e) => onToggleChange('weather_priority', e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <WeatherIcon fontSize="small" color={policyToggles.weather_priority ? 'success' : 'action'} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Weather Prioritization</Typography>
                                                <Typography variant="caption" color="text.secondary">Shift supply to snow-belt regions based on forecast</Typography>
                                            </Box>
                                        </Box>
                                    }
                                />
                            </Paper>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    bgcolor: policyToggles.incentive_protection ? 'rgba(255, 152, 0, 0.05)' : 'white',
                                    borderColor: policyToggles.incentive_protection ? 'warning.light' : 'divider',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            color="warning"
                                            checked={!!policyToggles.incentive_protection}
                                            onChange={(e) => onToggleChange('incentive_protection', e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <ProtectIcon fontSize="small" color={policyToggles.incentive_protection ? 'warning' : 'action'} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Incentive Protection</Typography>
                                                <Typography variant="caption" color="text.secondary">Prevent supply drops in high-incentive regions</Typography>
                                            </Box>
                                        </Box>
                                    }
                                />
                            </Paper>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default NationalParameterEditor;
